import { string } from 'prop-types';
import React from 'react';
import { createResourceLocatorString } from '../../../../util/routes';
import { encodeLatLngBounds } from '../../../../util/urlHelpers';
import TopbarSearchForm from '../../../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';
import css from './CustomerHero.module.css';
import classNames from 'classnames';

/**
 * CustomerHero Component
 *
 * This component serves as a search interface that allows users to find and book services
 * in their vicinity. It provides input fields for service keywords and location, as well as
 * a button to initiate the search.
 *
 * Props:
 * - searchQuery: Current value of the search input for services.
 * - setSearchQuery: Function to update the search query state.
 * - history: History object for programmatic navigation.
 * - routeConfiguration: Configuration object for defining routes.
 * - config: Configuration for the application used in search form.
 * - handleLocationSubmit: Function to handle the submission of location search.
 * - locationQuery: Current value for the location input (not used directly here).
 */
const CustomerHero = ({
  searchQuery,
  setSearchQuery,
  history,
  routeConfiguration,
  config,
  handleLocationSubmit,
  locationQuery,
  intl,
}) => {
  const selectedPlace = locationQuery?.location?.selectedPlace;
  const hasSelectedLocation = Boolean(selectedPlace?.bounds);
  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const runSearch = () => {
    const searchParams = {};
    const keywords = (searchQuery || '').trim();
    if (keywords) {
      searchParams.keywords = keywords;
    }
    if (hasSelectedLocation) {
      searchParams.address = locationQuery.location.search || '';
      searchParams.bounds = encodeLatLngBounds(selectedPlace.bounds);
    }

    history.push(
      createResourceLocatorString(
        'AlgoliaSearchPage',
        routeConfiguration,
        { listingType: 'deals' },
        searchParams
      )
    );
  };

  return (
    <div className={css.customerHeroWrapper}>
      <div className={classNames(css.customerSearch, arabicLanguage && css.arabicText)}>
        <h1 className={css.heroHeading}>
          {intl.formatMessage(
            { id: "CustomerHero.bestDealHeading" },
            {
              highlight: chunks => (
                <span className={css.heroHighlight}>
                  <span className={css.heroHighlightText}>{chunks}</span>
                  <span className={css.heroHighlightBar} aria-hidden="true" />
                </span>
              ),
              line: chunks => <span className={css.heroLine}>{chunks}</span>,
              br: () => <br />,
            }
          )}
        </h1>
        <p className={css.heroSubheading}>
          {intl.formatMessage({ id: "CustomerHero.findServicesHeading" })}
        </p>
        <div className={css.searchBar}>
          <div className={css.searchFields}>
            {/* Service field */}
            <label className={css.field} htmlFor="searchQuery">
              <span className={css.fieldLabel}>
                {intl.formatMessage({ id: 'CustomerHero.serviceNameLabel' })}
              </span>
              <input
                className={css.fieldInput}
                onChange={e => setSearchQuery(e.target.value)}
                name="searchQuery"
                id="searchQuery"
                value={searchQuery}
                type="text"
                placeholder={intl.formatMessage({ id: 'CustomerHero.serviceNamePlaceholder' })}
              />
            </label>

            <span className={css.fieldDivider} aria-hidden="true" />

            {/* Location field */}
            <div className={css.field}>
              <span className={css.fieldLabel}>
                {intl.formatMessage({ id: 'CustomerHero.locationLabel' })}
              </span>
              <TopbarSearchForm
                className={css.locationForm}
                desktopInputRoot={css.locationInputRoot}
                desktopPredictionsClassName={css.locationPredictions}
                onSubmit={handleLocationSubmit}
                initialValues={{}}
                appConfig={config}
              />
            </div>
          </div>

          {/* Find deals — separate yellow pill button */}
          <button
            type="button"
            className={css.searchButton}
            onClick={runSearch}
          >
            <svg
              className={css.searchButtonIcon}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" fill="none" />
              <path d="m21 21-4.3-4.3" fill="none" />
            </svg>
            {intl.formatMessage({ id: 'CustomerHero.search' })}
          </button>
        </div>
      </div>
    </div>
  );
};

// Default props for the component
CustomerHero.defaultProps = {
  className: null,
  rootClassName: null,
};

// Prop types for validation
CustomerHero.propTypes = {
  className: string,
  rootClassName: string,
};

export default CustomerHero;
